import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

const DAYS = [
  { id: 1, full: 'Понедельник' },
  { id: 2, full: 'Вторник' },
  { id: 3, full: 'Среда' },
  { id: 4, full: 'Четверг' },
  { id: 5, full: 'Пятница' },
];

export default function LessonEditModal({ editing, classRow, subjects, teachers, rooms = [], onClose, onSaved, onDeleted }) {
  const isNew = !!editing._isNew;

  // classRow может быть null (редактирование из режимов «учитель»/«кабинет»).
  // В этом случае берём class info из самого урока.
  const classId   = editing.classId || classRow?.id;
  const classCode = classRow?.code || editing.classCode || '?';
  const yearId    = classRow?.yearId || editing.yearId;

  const [year, setYear]               = useState(null);
  const [subjectId, setSubjectId]     = useState(() => isNew ? '' : String(editing.subjectId));
  const [teacherId, setTeacherId]     = useState(() => isNew ? '' : (editing.teacherId ? String(editing.teacherId) : ''));
  const [roomId, setRoomId]           = useState(() => isNew ? '' : (editing.roomId ? String(editing.roomId) : ''));
  const [note, setNote]               = useState(() => isNew ? '' : (editing.note || ''));
  const [effectiveFrom, setEffFrom]   = useState(() => editing.effectiveFrom || '');
  const [effectiveTo,   setEffTo]     = useState(() => editing.effectiveTo   || '');

  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [conflicts, setConflicts]     = useState(editing._moveConflict || null);

  // Подгружаем год для дефолтных дат и min/max
  useEffect(() => {
    if (!yearId) return;
    api.get('/api/years_study').then((res) => {
      const y = (res.rows || []).find((x) => x.id === yearId);
      if (y) {
        setYear(y);
        if (!effectiveFrom) setEffFrom(y.dateStart);
        if (!effectiveTo)   setEffTo(y.dateEnd);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const save = async (force = false) => {
    if (!subjectId) { setError('Выберите предмет'); return; }
    if (!effectiveFrom || !effectiveTo) { setError('Укажите период действия'); return; }
    setSaving(true); setError(null); setConflicts(null);
    try {
      const payload = {
        classId,
        weekday: editing.weekday,
        position: editing.position,
        subjectId: Number(subjectId),
        teacherId: teacherId ? Number(teacherId) : null,
        roomId: roomId ? Number(roomId) : null,
        note: note.trim() || null,
        effectiveFrom,
        effectiveTo,
        confirmConflict: force,
      };
      const res = isNew
        ? await api.post('/api/schedule', payload)
        : await api.put('/api/schedule/' + editing.id, payload);
      onSaved?.(res.lesson);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'conflict' && err.payload?.error?.conflicts) {
        setConflicts(err.payload.error.conflicts);
      } else {
        setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
      }
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (isNew) return;
    if (!confirm('Удалить урок?')) return;
    setSaving(true); setError(null);
    try {
      await api.delete('/api/schedule/' + editing.id);
      onDeleted?.(editing.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось'));
      setSaving(false);
    }
  };

  const dayLabel = DAYS.find((d) => d.id === editing.weekday)?.full || editing.weekday;

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>
            {isNew ? 'Новый урок' : 'Редактирование урока'}
            <span style={{ fontWeight: 400, color: 'var(--fg-muted)', fontSize: 13, marginLeft: 8 }}>
              · {classCode} · {dayLabel}, {editing.position} урок
            </span>
          </h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Предмет *</label>
              <select
                className="sd-edit__select"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                required autoFocus
              >
                <option value="">— выберите —</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.noJournal ? ' (без дневника)' : ''}</option>
                ))}
              </select>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Учитель</label>
              <select
                className="sd-edit__select"
                value={teacherId}
                onChange={(e) => setTeacherId(e.target.value)}
              >
                <option value="">— не назначен —</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.role === 'administrator' ? ' (админ)' : ''}</option>
                ))}
              </select>
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Кабинет</label>
              <select
                className="sd-edit__select"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
              >
                <option value="">— не выбран —</option>
                {rooms.filter((r) => !r.archive || String(r.id) === roomId).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}{r.archive ? ' (архив)' : ''}</option>
                ))}
              </select>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Примечание</label>
              <input className="sd-edit__input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="напр. подгруппа 2" />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Период действия — с *</label>
              <input
                className="sd-edit__input" type="date"
                value={effectiveFrom}
                min={year?.dateStart || undefined}
                max={year?.dateEnd   || undefined}
                onChange={(e) => setEffFrom(e.target.value)}
                required
              />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">по *</label>
              <input
                className="sd-edit__input" type="date"
                value={effectiveTo}
                min={effectiveFrom || year?.dateStart || undefined}
                max={year?.dateEnd  || undefined}
                onChange={(e) => setEffTo(e.target.value)}
                required
              />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <div className="sd-edit__hint">
                Урок попадает в недели, у которых пересекается период с этим. По умолчанию — весь учебный год.
              </div>
            </div>

            {conflicts && (
              <div className="sd-edit__field sd-edit__field--full">
                <div className="sch-conflict-block">
                  <div className="sch-conflict-block__title">
                    <Icon name="alert" size={13} /> Конфликт расписания
                  </div>
                  <ul>
                    {conflicts.map((c, i) => (<li key={i}>{c.detail}</li>))}
                  </ul>
                  <div className="sch-conflict-block__hint">
                    Можно сохранить «как есть» — но один и тот же учитель/кабинет
                    реально не сможет вести два класса в этот же слот.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {!isNew && (
              <button type="button" className="btn btn--sm btn--danger" onClick={onDelete} disabled={saving}>Удалить</button>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            {conflicts ? (
              <button type="button" className="btn btn--sm btn--danger" onClick={() => save(true)} disabled={saving}>
                {saving ? 'Сохраняем…' : 'Всё равно сохранить'}
              </button>
            ) : (
              <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
                {saving ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
